import { KindType } from '@/__generated__/gateway/graphql';
import { queryTabCounters } from '@/shared/graphql/gateway/queries';
import { useProductStore } from '@/shared/pinia/modules/product';
import {
  getEntityFromContext,
  getEntityIdFromContext,
  getLanguageFromContext
} from '@/shared/utils/gateway/context';

import { patternSuggestionsForYarnQuery } from '../../shared/composables/yarnPatterns/yarnPatternsGateway.queries';
import { searchPatternsBySKUsQuery } from '../../shared/composables/yarnPatterns/yarnPatternsStorefront.queries';
import { getApolloClients } from '../../shared/utils/apollo-client';
import { getCountryCode } from '../../shared/utils/country';

const initTabCounters = async () => {
  const galleryTabs = document.querySelectorAll('.js-gallery-button-label');
  const qaTabs = document.querySelectorAll('.js-qa-button-label');

  if (!galleryTabs.length && !qaTabs.length) return;

  try {
    const entity = getEntityFromContext();
    const entity_id = getEntityIdFromContext();
    const language = getLanguageFromContext();

    const queryArgs = {
      galleryContext: { kind: KindType.Gallery, entity, entity_id, language },
      qaContext: { kind: KindType.Qa, entity, entity_id, language }
    };

    const { gateway } = await getApolloClients();
    const { data } = await gateway.query(queryTabCounters(queryArgs));

    galleryTabs.forEach((galleryTab) => {
      galleryTab.innerHTML = galleryTab.innerHTML.replace(
        '0',
        String(data.community?.gallery?.total ?? 0)
      );
    });

    qaTabs.forEach((qaTab) => {
      qaTab.innerHTML = qaTab.innerHTML.replace(
        '0',
        String(data.community?.qa?.total ?? 0)
      );
    });
  } catch (error) {
    console.error(error);
  }
};

const initYarnPatternsTabCount = async () => {
  const yarnPatternsTabs = document.querySelectorAll(
    '.js-yarn-patterns-button-label'
  );

  if (!yarnPatternsTabs.length) return;

  const productStore = useProductStore();
  const productGroupId = productStore.resolvedEntityId;

  if (!productGroupId) return;

  try {
    // Get apollo clients
    const { gateway: gatewayClient, storefront: storefrontClient } =
      await getApolloClients();

    // Step 1: Fetch pattern SKUs from Gateway API
    const gatewayResult = await gatewayClient.query({
      query: patternSuggestionsForYarnQuery,
      variables: {
        yarnProductGroupIdentifier: productGroupId,
        locale: window?.Shopify?.locale || 'en',
        limit: 250
      }
    });

    const patterns =
      gatewayResult.data?.crafter?.patternSuggestionsForYarn?.patterns || [];

    if (patterns.length === 0) {
      updateYarnPatternsTabCount(0);
      return;
    }

    // Step 2: Search for products by pattern SKUs in Shopify
    const skus = patterns.map((p) => p.sku).filter(Boolean);
    if (skus.length === 0) {
      updateYarnPatternsTabCount(0);
      return;
    }

    const searchQuery = skus.map((sku) => `variants.sku:${sku}`).join(' OR ');
    const storefrontResult = await storefrontClient.query({
      query: searchPatternsBySKUsQuery,
      variables: {
        searchQuery,
        country: getCountryCode()
      }
    });

    const searchProducts = storefrontResult.data?.search?.nodes || [];

    // Step 3: Count how many patterns have matching products
    let matchedCount = 0;
    patterns.forEach((gp) => {
      // Filter to only Product results (exclude Article and Page)
      const products = searchProducts.filter(
        (
          result
        ): result is Extract<typeof result, { __typename?: 'Product' }> =>
          result.__typename === 'Product'
      );
      const product = products.find((p) => {
        const metafieldMatch = p.metafields?.find(
          (m) => m?.key === 'sku' && m?.value === gp.sku
        );
        if (metafieldMatch) return true;
        const variantMatch = p.variants?.nodes?.find((v) => v.sku === gp.sku);
        return !!variantMatch;
      });
      if (product) matchedCount++;
    });

    updateYarnPatternsTabCount(matchedCount);
  } catch (error) {
    console.error('Error fetching yarn patterns count:', error);
    updateYarnPatternsTabCount(0);
  }
};

const updateYarnPatternsTabCount = (count: number) => {
  const yarnPatternsTabs = document.querySelectorAll(
    '.js-yarn-patterns-button-label'
  );

  yarnPatternsTabs.forEach((tab) => {
    // Replace the (0) with the actual count
    tab.innerHTML = tab.innerHTML.replace(/\(\d+\)/, `(${count})`);
  });
};

export { initTabCounters, initYarnPatternsTabCount };
